import images from '@assets/images/index'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PaginationState } from '@tanstack/react-table'
import { Col, Flex, Input, Row, Spin } from 'antd'
import { Resizable } from 're-resizable'
import { useEffect, useState } from 'react'

import {
  Entities_Notification_Get_NotificationData,
  Entities_Notification_Sent_Response_NotificationItem,
  LoginId
} from '@suntex.fim/api-client'
import { isPlayerLoginType } from '@suntex.fim/auth'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { EntityLevel, NotificationType } from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import {
  MessageContent,
  MessageListTool,
  MessageTab,
  SendMessage,
  SentMessageTab,
  useMessageActions,
  useNotificationCount
} from '@suntex.fim/messages'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { Button, TablePagination } from '@suntex.fim/ui'

function TeacherMessages() {
  const { api } = useFramework()
  useRotateDevice()
  const { login } = useAuth(isAuthenticated)
  const { id: loginId, entityLevel } = login

  const team = useOptionalTeamInfo()
  const { data: countData, refetch: refetchCount } =
    useNotificationCount(loginId)
  const count = countData ?? { count: 0 }
  const { deleteMessages } = useMessageActions()

  const [mode, setMode] = useState('inbox')
  const [sendMessageModal, setSendMessageModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [messages, setMessages] = useState<
    (
      | Entities_Notification_Get_NotificationData
      | Entities_Notification_Sent_Response_NotificationItem
    )[]
  >([])
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 25,
    pageIndex: 0
  })
  const [activeMessageId, setActiveMessageId] = useState<number>(0)
  const [selectedMessages, setSelectedMessages] = useState<
    Record<number, boolean>
  >({})
  const [allSelected, setAllSelected] = useState(false)
  const isAdminRole = entityLevel === EntityLevel.All
  const canDelete = !isAdminRole && !isPlayerLoginType(login.loginType)

  const queryClient = useQueryClient()

  const SEARCH_DEBOUNCE_MS = 500
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(searchQuery),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data: inboxData, isLoading: isInboxLoading } = useQuery({
    queryKey: [
      'notifications',
      'inbox',
      loginId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedSearchQuery
    ],
    queryFn: async () => {
      const response = await api.get_Notification_Notifications({
        loginId,
        notificationType: NotificationType.All,
        pageNumber: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        searchQuery: debouncedSearchQuery
      })
      return {
        messages: response.allNotificationData || [],
        totalCount: response.totalCount || 0
      }
    },
    enabled: !!loginId
  })

  const { data: outboxData, isLoading: isOutboxLoading } = useQuery({
    queryKey: [
      'notifications',
      'outbox',
      loginId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedSearchQuery
    ],
    queryFn: async () => {
      const response = await api.get_Notification_Sent({
        loginId,
        pageNumber: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        searchQuery: debouncedSearchQuery
      })
      return {
        messages: response.notifications || [],
        totalCount: response.totalCount || 0
      }
    },
    enabled: !!loginId
  })

  const { data: selectedUserOptions = [] } = useQuery({
    queryKey: ['assessment', 'summaryTeam', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_SummaryTeam({
        entityId: team!.id
      })
      return (response.playerSummaries || []).map((original) => ({
        label: original.displayName,
        value: original.id
      }))
    },
    enabled: !!team?.id
  })

  const reloadInbox = () => {
    void queryClient.invalidateQueries({
      queryKey: ['notifications', 'inbox', loginId]
    })
  }
  const reloadOutbox = () => {
    void queryClient.invalidateQueries({
      queryKey: ['notifications', 'outbox', loginId]
    })
  }

  const inboxMessages = inboxData?.messages ?? []
  const outboxMessages = outboxData?.messages ?? []
  const inputMessageCount = inboxData?.totalCount ?? 0
  const sentMessageCount = outboxData?.totalCount ?? 0
  const isLoading = mode === 'inbox' ? isInboxLoading : isOutboxLoading

  useEffect(() => {
    if (mode === 'inbox') {
      setMessages(inboxMessages)
    } else {
      setMessages(outboxMessages)
    }
  }, [mode, inboxMessages, outboxMessages])

  const activeMessageContent = messages.find(
    (message) => message && message.id === activeMessageId
  )

  const onMarkedAsRead = (notificationId: string | number) => {
    void refetchCount()
    const index = messages.findIndex(
      (message) => message && message.id === notificationId
    )
    if (index >= 0) {
      const newMessages = [...messages]
      const target = newMessages[index]
      if (target) target.unread = false
      setMessages(newMessages)
    }
  }

  const deleteMessage = async (
    notificationId: number | number[],
    loginId: LoginId
  ) => {
    try {
      await deleteMessages(notificationId, loginId)
    } finally {
      setAllSelected(false)
      setSelectedMessages({})
    }
  }

  const handleSelect = (checked: boolean, id: number) => {
    const newSelectedList = { ...selectedMessages, [id]: checked }
    setSelectedMessages(newSelectedList)
    let isAllSelected = true
    if (Object.keys(selectedMessages).length !== messages.length) {
      isAllSelected = false
    } else {
      messages.forEach(({ id = 0 }) => {
        if (!newSelectedList[id]) {
          isAllSelected = false
        }
      })
    }
    setAllSelected(isAllSelected)
  }

  const handleAllSelect = (checked: boolean) => {
    const newSelectedList = messages.reduce<Record<number, boolean>>(
      (list, { id = 0 }) => ({
        ...list,
        [id]: checked
      }),
      {}
    )
    setSelectedMessages(newSelectedList)
    setAllSelected(checked)
  }

  const shouldShowPagination =
    (mode === 'inbox' && inputMessageCount > 0) ||
    (mode === 'outbox' && sentMessageCount > 0)

  return (
    <>
      <div className="main-heading">
        <h1>Messages</h1>
        <div className="tabs">
          <ul className="tabs-nav-list">
            <li
              className={`tabs-btn ${
                mode === 'inbox' ? 'tabs-btn-active' : ''
              }`}
            >
              <Flex
                gap={10}
                className="tabs-btn-wrap"
                onClick={() => {
                  setMode('inbox')
                  setPagination((prev) => ({
                    ...prev,
                    pageIndex: 0,
                    pageSize: 25
                  }))
                }}
              >
                <Flex gap={10} align="center" className="tabs-btn-icon">
                  <images.DirectInbox />{' '}
                </Flex>
                <Flex gap={10} align="center">
                  <span>{!isAdminRole ? 'Inbox' : 'User Feedback'}</span>
                  <span className="count">{count.count}</span>
                </Flex>
              </Flex>
            </li>
            <li
              className={`tabs-btn ${
                mode === 'outbox' ? 'tabs-btn-active' : ''
              }`}
            >
              <Flex
                gap={10}
                className="tabs-btn-wrap sent-btn"
                onClick={() => {
                  setMode('outbox')
                  setPagination((prev) => ({
                    ...prev,
                    pageIndex: 0,
                    pageSize: 25
                  }))
                }}
              >
                <Flex gap={10} align="center" className="tabs-btn-icon">
                  <images.DirectSend />{' '}
                </Flex>
                <Flex gap={10} align="center">
                  <span>{!isAdminRole ? 'Sent' : 'App Feedback'}</span>
                </Flex>
              </Flex>
            </li>
          </ul>
        </div>
        <Flex gap={16} className="message-input-box">
          <div>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<images.SearchIcon />}
              className="suggest-content__sidebar-search"
              aria-label="Search messages"
            />
          </div>
          {!isAdminRole && (
            <Button
              shape="round"
              intent="tertiary"
              onClick={() => setSendMessageModal(true)}
            >
              Send a Message
            </Button>
          )}
        </Flex>
      </div>
      {isLoading && !isAdminRole ? (
        <Spin />
      ) : (
        <Row className="main-inbox">
          <Resizable
            className="message-resize-box"
            boundsByDirection={false}
            enable={{
              top: false,
              right: true,
              bottom: false,
              left: false,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false
            }}
          >
            <Col flex="400px" className="message-list message-list-left">
              <MessageListTool
                handleAllSelect={handleAllSelect}
                allSelected={allSelected}
                selectedMessages={selectedMessages}
                onMarkedAsRead={onMarkedAsRead}
                deleteMessage={deleteMessage}
                loginId={loginId}
                mode={mode}
                canDelete={canDelete}
              />
              {mode === 'inbox' &&
                inboxMessages
                  .filter(
                    (message) => message !== null && message !== undefined
                  )
                  .map(
                    ({
                      id = 0,
                      unread,
                      message,
                      senderDisplayName,
                      created,
                      modified
                    }) => (
                      <MessageTab
                        title={senderDisplayName || ''}
                        key={id}
                        active={id === activeMessageId}
                        read={!unread}
                        message={message || ''}
                        onClick={() => setActiveMessageId(id)}
                        created={created}
                        modified={modified}
                        checked={selectedMessages[id] ?? false}
                        handleSelect={(checked) => handleSelect(checked, id)}
                      />
                    )
                  )}
              {mode === 'outbox' &&
                outboxMessages
                  .filter(
                    (message) => message !== null && message !== undefined
                  )
                  .map(
                    ({
                      id = 0,
                      unread,
                      message,
                      receiverDisplayName,
                      modified
                    }) => (
                      <SentMessageTab
                        key={id}
                        title={receiverDisplayName || ''}
                        active={id === activeMessageId}
                        read={!unread}
                        message={message || ''}
                        onClick={() => setActiveMessageId(id)}
                        modified={modified}
                        checked={selectedMessages[id] ?? false}
                        handleSelect={(checked) => handleSelect(checked, id)}
                      />
                    )
                  )}
              {(mode === 'inbox' || mode === 'outbox') &&
              shouldShowPagination ? (
                <div className="message-box-pagination">
                  <TablePagination
                    manualPagination={false}
                    page={pagination.pageIndex + 1}
                    pageSize={pagination.pageSize}
                    totalEntries={
                      mode === 'inbox' ? inputMessageCount : sentMessageCount
                    }
                    totalRecord={
                      mode === 'inbox' ? inputMessageCount : sentMessageCount
                    }
                    isLoading={false}
                    setPage={(pageIndex) => {
                      setPagination((prev) => ({
                        ...prev,
                        pageIndex: pageIndex - 1
                      }))
                    }}
                    setPageSize={(pageSize) => {
                      setPagination((prev) => ({
                        ...prev,
                        pageIndex: 0,
                        pageSize
                      }))
                    }}
                  />
                </div>
              ) : null}
            </Col>
          </Resizable>
          <Col flex="auto" className="message-body">
            {activeMessageContent && (
              <MessageContent
                message={activeMessageContent.message}
                senderDisplayName={
                  ('senderDisplayName' in activeMessageContent
                    ? activeMessageContent.senderDisplayName
                    : '') || ''
                }
                unread={!!activeMessageContent.unread}
                notificationId={activeMessageId}
                modified={
                  activeMessageContent.modified ?? activeMessageContent.created
                }
                onMarkedAsRead={onMarkedAsRead}
                user={login}
                senderLoginId={activeMessageContent.senderLoginId}
                deleteMessage={deleteMessage}
                mode={mode}
                receiverDisplayName={
                  ('receiverDisplayName' in activeMessageContent
                    ? activeMessageContent.receiverDisplayName
                    : '') || ''
                }
                loginId={activeMessageContent.loginId || loginId}
                isAdminRole={isAdminRole}
                canDelete={canDelete}
              />
            )}
          </Col>
        </Row>
      )}
      <SendMessage
        isModalOpen={sendMessageModal}
        closeModal={() => {
          setSendMessageModal(false)
          reloadOutbox()
          setMode('outbox')
        }}
        selectedUserOptions={selectedUserOptions}
      />
    </>
  )
}

export default TeacherMessages
